import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Save } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

export function LoginPageTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [err, setErr] = useState("");
  const logoRef = useRef();
  const confirm = useConfirm();

  const load = () => api.get("/login-page").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="p-6 text-slate-400 text-sm">Loading…</div>;

  const update = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      await api.put("/login-page", {
        headline: cfg.headline || "",
        subtitle: cfg.subtitle || "",
        welcome_text: cfg.welcome_text || "",
        footer_text: cfg.footer_text || "",
        show_demo_hint: !!cfg.show_demo_hint,
      });
      setInfo("Saved. Refresh /login to see changes.");
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail?.toString() || ex.message);
    } finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setErr(""); setInfo("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/login-page/logo", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setInfo("Logo uploaded.");
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail?.toString() || ex.message);
    } finally { setBusy(false); }
  };

  const removeLogo = async () => {
    const ok = await confirm({
      title: "Remove the custom logo?",
      description: "We'll revert to the default Entry Group logo.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true); setErr(""); setInfo("");
    try {
      await api.delete("/login-page/logo");
      setInfo("Reverted to default logo.");
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail?.toString() || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="login-page-tab">
      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Headline panel (left side of login screen)</div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Headline</label>
          <input value={cfg.headline || ""} onChange={(e) => update({ headline: e.target.value })} className="qb-input" data-testid="login-cfg-headline" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Subtitle</label>
          <textarea rows={3} value={cfg.subtitle || ""} onChange={(e) => update({ subtitle: e.target.value })} className="qb-input" data-testid="login-cfg-subtitle" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Welcome / notice (optional)</label>
          <textarea rows={3} value={cfg.welcome_text || ""} onChange={(e) => update({ welcome_text: e.target.value })} className="qb-input" placeholder="e.g. Maintenance window: every Sunday 02:00–04:00 GMT" data-testid="login-cfg-welcome" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Footer text</label>
          <input value={cfg.footer_text || ""} onChange={(e) => update({ footer_text: e.target.value })} className="qb-input" data-testid="login-cfg-footer" />
        </div>
      </div>

      <div className="qb-card p-5 space-y-3">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Logo</div>
        <div className="flex items-center gap-4">
          <div className="bg-slate-100 rounded-md p-2">
            <img
              src={cfg.has_custom_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/login-page/logo?ts=${cfg.updated_at || ""}` : "/brand/entry-group-logo.jpeg"}
              alt="Login logo"
              className="h-14 w-auto rounded"
              onError={(e) => { e.currentTarget.src = "/brand/entry-group-logo.jpeg"; }}
              data-testid="login-cfg-logo-preview"
            />
          </div>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="login-cfg-logo-input" />
          <button onClick={() => logoRef.current?.click()} disabled={busy} className="h-9 px-4 rounded-full border border-brand-blue text-brand-blue text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue hover:text-white disabled:opacity-60" data-testid="login-cfg-logo-upload">Upload logo</button>
          {cfg.has_custom_logo && (
            <button onClick={removeLogo} disabled={busy} className="h-9 px-4 rounded-full border border-slate-300 text-slate-600 text-xs uppercase tracking-wider font-semibold hover:border-red-300 hover:text-red-600" data-testid="login-cfg-logo-remove">Remove custom logo</button>
          )}
        </div>
        <p className="text-[11px] text-slate-500">PNG or JPG up to 4 MB. Shown next to the "Quote Matrix" wordmark on the sign-in screen.</p>
      </div>

      {info && <div className="border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 rounded-lg" data-testid="login-cfg-info">{info}</div>}
      {err && <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="login-cfg-error">{err}</div>}

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark disabled:opacity-60 rounded-full" data-testid="login-cfg-save">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
